// v2/pages/LoginV2.js — v2 giris ekrani (/v2/login)
// Backend: POST /api/v2/auth/login/ (v2_auth.py). Borc engeli SUNUCUDA uygulanir:
// engelli cariye token verilmez; burada yalnizca bilgi penceresi gosterilir.
// "Beni hatirla": eski ekranla ayni (e-posta + parola 30 gun, ayni localStorage anahtari).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, AlertCircle, AlertTriangle, ShieldCheck,
  Boxes, Percent, Clock, CheckCircle2, X, CreditCard, Calendar, KeyRound, Phone,
} from 'lucide-react';
import { apiPost } from '../utils/api';
import { oturumBilgisi, oturumKaydet, hatirlananBilgi } from '../utils/authStore';
import { formatTRY } from '../utils/format';
import { KVKK_BASLIK, KVKK_METIN } from '../constants/kvkk';
import s from '../styles/LoginV2.module.css';
import '../styles/tokens.css';

const ANA = '/v2';
const YONETIM = '/v2/admin';
const HATIRLA_GUN = 30;
const BOSCH_ODEME = 'https://www.bosch-tahsilat.com/';
const DESTEK_TEL = '+90 (342) 235 00 24';
const DESTEK_TEL_HREF = 'tel:+903422350024';
const DESTEK_MAIL = 'info@as-otomotiv.com.tr';

const OZELLIKLER = [
  { ikon: Boxes, baslik: 'Anlık stok', metin: 'Gaziantep ve İstanbul depolarından canlı stok bilgisi' },
  { ikon: Percent, baslik: 'Size özel fiyatlar', metin: 'Cariye özel indirimler ve dönemsel kampanyalar' },
  { ikon: Clock, baslik: '7/24 sipariş', metin: 'Siparişleriniz doğrudan sisteme iletilir' },
  { ikon: ShieldCheck, baslik: '%100 orijinal', metin: 'Yetkili satıcıdan orijinal Bosch ürünleri' },
];

function tarihTR(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function sureYaz(sn) {
  const k = Math.max(0, sn);
  return `${String(Math.floor(k / 60)).padStart(2, '0')}:${String(k % 60).padStart(2, '0')}`;
}

function hedefYolu(rol, from) {
  if (rol === 'admin') return YONETIM;
  if (typeof from === 'string' && from.startsWith('/v2')
      && !from.startsWith('/v2/admin') && !from.startsWith('/v2/login')) return from;
  return ANA;
}

/** Ortak pencere kabugu: Esc, arka plan tiklamasi, sayfa kaydirma kilidi */
function Pencere({ etiket, onKapat, kapatilabilir = true, genis = false, children }) {
  useEffect(() => {
    const onceki = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const esc = (e) => { if (e.key === 'Escape' && kapatilabilir && onKapat) onKapat(); };
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = onceki;
      document.removeEventListener('keydown', esc);
    };
  }, [kapatilabilir, onKapat]);
  return (
    <div className={s.overlay} role="dialog" aria-modal="true" aria-label={etiket}>
      <div className={s.backdrop} onClick={() => kapatilabilir && onKapat && onKapat()} aria-hidden="true" />
      <div className={`${s.modal} ${genis ? s.modalWide : ''}`}>{children}</div>
    </div>
  );
}

function BorcPenceresi({ r, onDevam, onKapat }) {
  const engel = r.type === 'blocked';
  const btnRef = useRef(null);
  useEffect(() => { if (btnRef.current) btnRef.current.focus(); }, []);
  const tarih = tarihTR(r.dueDate);

  return (
    <Pencere etiket={engel ? 'Erişim durduruldu' : 'Ödeme hatırlatması'} onKapat={engel ? onKapat : onDevam}>
      <div className={s.modalHead}>
        <span className={`${s.mIcon} ${engel ? s.mIconRed : s.mIconAmber}`}>
          {engel ? <Lock size={26} /> : <AlertTriangle size={26} />}
        </span>
        <h3 className={s.mTitle}>{engel ? 'Erişiminiz geçici olarak durduruldu' : 'Ödeme hatırlatması'}</h3>
        {r.message && <p className={s.mText}>{r.message}</p>}
      </div>

      {!engel && r.daysLeft !== null && r.daysLeft !== undefined && (
        <div className={s.daysBox}>
          <span className={s.daysNum}>{r.daysLeft}</span>
          <span className={s.daysLbl}>
            gün kaldı{tarih ? <> · son ödeme <b>{tarih}</b></> : null}
          </span>
        </div>
      )}
      {engel && tarih && (
        <div className={s.due}><Calendar size={14} /> Son ödeme tarihi: <b>{tarih}</b></div>
      )}

      {r.requiredPayment > 0 && (
        <div className={`${s.amountBox} ${engel ? s.amountRed : ''}`}>
          <span className={s.amountLabel}>
            {engel ? 'Erişimin açılması için gereken minimum ödeme' : 'Kısıtlamanın kalkması için gereken minimum ödeme'}
          </span>
          <span className={s.amountVal}>{formatTRY(r.requiredPayment)}</span>
          <span className={s.amountMeta}>
            Vadesi geçmiş borç {formatTRY(r.overdueDebt)} · Net bakiye {formatTRY(r.netBalance)}
          </span>
        </div>
      )}

      <div className={s.mActions}>
        <a className={s.payBtn} href={r.paymentUrl || BOSCH_ODEME} target="_blank" rel="noopener noreferrer">
          <CreditCard size={17} /> Bosch Tahsilat ile Ödeme Yap
        </a>
        {engel ? (
          <button ref={btnRef} type="button" className={s.ghostBtn} onClick={onKapat}>Kapat</button>
        ) : (
          <button ref={btnRef} type="button" className={s.primaryBtn} onClick={onDevam}>
            Anladım, devam et <ArrowRight size={16} />
          </button>
        )}
      </div>
      <p className={s.mNote}>
        {engel
          ? 'Ödemeniz sisteme yansıdığında erişiminiz otomatik olarak açılır.'
          : 'Süre sonunda ödeme yapılmazsa sisteme erişiminiz durdurulur.'}
        {' '}Sorularınız için <a href={`mailto:${DESTEK_MAIL}`}>{DESTEK_MAIL}</a>
      </p>
    </Pencere>
  );
}

function KvkkPenceresi({ onKapat, onOnayla }) {
  return (
    <Pencere etiket={KVKK_BASLIK} onKapat={onKapat} genis>
      <div className={s.kvkkHead}>
        <span className={`${s.mIcon} ${s.mIconBlue}`}><ShieldCheck size={22} /></span>
        <h3 className={s.kvkkTitle}>{KVKK_BASLIK}</h3>
        <button type="button" className={s.closeBtn} onClick={onKapat} aria-label="Kapat"><X size={18} /></button>
      </div>
      <div className={s.kvkkBody}>
        {KVKK_METIN.map((b, i) => (
          <section key={i}>
            {b.baslik && <h4>{b.baslik}</h4>}
            {b.paragraflar.map((p, j) => <p key={j}>{p}</p>)}
          </section>
        ))}
      </div>
      <div className={s.kvkkFoot}>
        <button type="button" className={s.ghostBtn} onClick={onKapat}>Kapat</button>
        <button type="button" className={s.primaryBtn} onClick={onOnayla}>
          <CheckCircle2 size={16} /> Okudum, onaylıyorum
        </button>
      </div>
    </Pencere>
  );
}

export default function LoginV2() {
  const nav = useNavigate();
  const konum = useLocation();
  const oturum = oturumBilgisi();

  const [email, setEmail] = useState('');
  const [sifre, setSifre] = useState('');
  const [goster, setGoster] = useState(false);
  const [hatirla, setHatirla] = useState(false);
  const [kvkk, setKvkk] = useState(false);
  const [kvkkAcik, setKvkkAcik] = useState(false);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);           // { mesaj, kilit?, kvkk? }
  const [sallan, setSallan] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [kilitBitis, setKilitBitis] = useState(0);
  const [simdi, setSimdi] = useState(() => Date.now());
  const [borc, setBorc] = useState(null);           // { r, hedef }

  const gonderKilit = useRef(false);
  const emailRef = useRef(null);
  const sifreRef = useRef(null);

  // "Beni hatirla": eski ekranla ayni kayit (30 gun)
  useEffect(() => {
    const h = hatirlananBilgi();
    if (h && h.email && Date.now() - (h.timestamp || 0) < HATIRLA_GUN * 864e5) {
      setEmail(h.email);
      setSifre(h.password || '');
      setHatirla(true);
      setKvkk(true);
    } else if (h) {
      try { window.localStorage.removeItem('rememberMe'); } catch (e) { /* yok say */ }
    }
    const t = setTimeout(() => {
      const hedef = (h && h.email) ? sifreRef.current : emailRef.current;
      if (hedef && !(h && h.password)) hedef.focus();
    }, 80);
    return () => clearTimeout(t);
  }, []);

  // v2 icindeyken borc engeline takilip cikarildiysa (V2Shell) engel penceresini goster
  useEffect(() => {
    try {
      const ham = window.sessionStorage.getItem('v2_borc_engel');
      if (ham) {
        window.sessionStorage.removeItem('v2_borc_engel');
        const r = JSON.parse(ham);
        if (r && r.type === 'blocked') setBorc({ r, hedef: null });
      }
    } catch (e) { /* yok say */ }
  }, []);

  // kilit geri sayimi
  useEffect(() => {
    if (!kilitBitis) return undefined;
    const t = setInterval(() => {
      const n = Date.now();
      setSimdi(n);
      if (n >= kilitBitis) { setKilitBitis(0); setHata(null); }
    }, 1000);
    return () => clearInterval(t);
  }, [kilitBitis]);
  const kilitKalan = kilitBitis ? Math.max(0, Math.ceil((kilitBitis - simdi) / 1000)) : 0;

  const salla = useCallback(() => {
    setSallan(false);
    requestAnimationFrame(() => setSallan(true));
  }, []);

  const capsKontrol = (e) => {
    if (e && typeof e.getModifierState === 'function') setCapsLock(e.getModifierState('CapsLock'));
  };

  const gonder = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (gonderKilit.current || kilitKalan > 0) return;
    const ep = email.trim();
    if (!ep || !sifre) {
      setHata({ mesaj: 'E-posta adresinizi ve şifrenizi girin.' });
      salla();
      return;
    }
    if (!kvkk) {
      setHata({ mesaj: 'Devam etmek için KVKK Aydınlatma Metni’ni onaylayın.', kvkk: true });
      salla();
      return;
    }
    gonderKilit.current = true;
    setYukleniyor(true);
    setHata(null);
    try {
      const v = await apiPost('/v2/auth/login/', { email: ep, password: sifre, kvkk: true }, { oturumsuz: true });
      if (!v || !v.success || !v.user || !v.user.token) {
        throw new Error((v && v.message) || 'Giriş yapılamadı');
      }
      oturumKaydet(v.user, { rememberMe: hatirla, email: ep, password: sifre });
      const hedef = hedefYolu(v.role, konum.state && konum.state.from);
      if (v.restriction && v.restriction.type === 'warning') {
        setBorc({ r: v.restriction, hedef });
        return;
      }
      nav(hedef, { replace: true });
    } catch (err) {
      const d = (err && err.data) || {};
      if (d.code === 'DEBT_BLOCKED' && d.restriction) {
        setBorc({ r: d.restriction, hedef: null });
      } else if (d.code === 'LOCKED') {
        setKilitBitis(Date.now() + (Number(d.retryAfter) || 900) * 1000);
        setSimdi(Date.now());
        setHata({ mesaj: d.message || 'Çok fazla hatalı deneme yapıldı.', kilit: true });
        salla();
      } else {
        const agYok = err && err.status === 0;
        setHata({
          mesaj: agYok
            ? 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.'
            : (d.message || (err && err.message) || 'Giriş yapılamadı'),
          kvkk: d.code === 'KVKK',
        });
        salla();
        if (d.code === 'INVALID') {
          setSifre('');
          if (sifreRef.current) sifreRef.current.focus();
        }
      }
    } finally {
      gonderKilit.current = false;
      setYukleniyor(false);
    }
  }, [email, sifre, kvkk, hatirla, kilitKalan, konum.state, nav, salla]);

  const borcDevam = useCallback(() => {
    const hedef = borc && borc.hedef;
    setBorc(null);
    if (hedef) nav(hedef, { replace: true });
  }, [borc, nav]);
  const borcKapat = useCallback(() => setBorc(null), []);
  const kvkkKapat = useCallback(() => setKvkkAcik(false), []);
  const kvkkOnayla = useCallback(() => { setKvkk(true); setKvkkAcik(false); setHata(null); }, []);

  // zaten oturum aciksa (uyari penceresi acik degilken) dogrudan panele
  if (oturum.girisli && !borc) {
    return <Navigate to={oturum.admin ? YONETIM : ANA} replace />;
  }

  const yil = new Date().getFullYear();

  return (
    <div className={s.page}>
      <aside className={s.brand} aria-hidden="false">
        <span className={s.glowA} aria-hidden="true" />
        <span className={s.glowB} aria-hidden="true" />
        <span className={s.gridBg} aria-hidden="true" />
        <div className={s.brandInner}>
          <div className={s.logos}>
            <span className={s.logoCard}><img className={s.logoImg} src="/asotomotivlogo_web.png" alt="AS Otomotiv" /></span>
            <span className={s.logoSep} aria-hidden="true" />
            <span className={s.logoCard}><img className={s.logoImg} src="/bosch.png" alt="Bosch" /></span>
          </div>

          <div className={s.hero}>
            <span className={s.kicker}>B2B Bayi Platformu</span>
            <h1 className={s.heroTitle}>
              Bosch yedek parçada <span className={s.heroAccent}>tüm siparişleriniz</span> tek ekranda.
            </h1>
            <p className={s.heroText}>
              Stok, fiyat, sipariş ve cari hesabınızı dilediğiniz yerden, dilediğiniz zaman yönetin.
            </p>
          </div>

          <ul className={s.features}>
            {OZELLIKLER.map(({ ikon: Ikon, baslik, metin }) => (
              <li key={baslik} className={s.feature}>
                <span className={s.featIcon}><Ikon size={18} /></span>
                <span>
                  <span className={s.featTitle}>{baslik}</span>
                  <span className={s.featText}>{metin}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className={s.brandFoot}>
            <span className={s.copy}>© {yil} AS Otomotiv</span>
            <span className={s.madeBy}>
              <img className={s.efLogo} src="/eforinnovate_siyaharka_web.png" alt="EforInnovate" />
            </span>
          </div>
        </div>
      </aside>

      <main className={s.formSide}>
        <div className={s.mobileBrand}>
          <span className={s.logoCard}><img className={s.logoImg} src="/asotomotivlogo_web.png" alt="AS Otomotiv" /></span>
          <span className={s.logoCard}><img className={s.logoImg} src="/bosch.png" alt="Bosch" /></span>
        </div>

        <form
          className={`${s.card} ${sallan ? s.shake : ''}`}
          onSubmit={gonder}
          onAnimationEnd={() => setSallan(false)}
          noValidate
        >
          <div className={s.cardHead}>
            <span className={s.cardIcon}><KeyRound size={20} /></span>
            <h2 className={s.title}>Hoş geldiniz</h2>
            <p className={s.sub}>Bayi hesabınızla giriş yapın</p>
          </div>

          {hata && (
            <div className={`${s.alert} ${hata.kilit ? s.alertLock : ''}`} role="alert">
              <AlertCircle size={17} />
              <span>
                {hata.mesaj}
                {kilitKalan > 0 && <b className={s.timer}> {sureYaz(kilitKalan)}</b>}
              </span>
            </div>
          )}

          <label className={s.field}>
            <span className={s.label}>E-posta</span>
            <span className={s.inputWrap}>
              <Mail size={17} className={s.inputIcon} />
              <input
                ref={emailRef} className={s.input} type="email" name="username"
                autoComplete="username" inputMode="email" spellCheck={false}
                placeholder="ornek@firma.com" value={email}
                onChange={(e) => setEmail(e.target.value)} disabled={yukleniyor}
              />
            </span>
          </label>

          <label className={s.field}>
            <span className={s.label}>Şifre</span>
            <span className={s.inputWrap}>
              <Lock size={17} className={s.inputIcon} />
              <input
                ref={sifreRef} className={`${s.input} ${s.inputPw}`}
                type={goster ? 'text' : 'password'} name="password"
                autoComplete="current-password" placeholder="Şifreniz" value={sifre}
                onChange={(e) => setSifre(e.target.value)}
                onKeyDown={capsKontrol} onKeyUp={capsKontrol}
                onBlur={() => setCapsLock(false)} disabled={yukleniyor}
              />
              <button
                type="button" className={s.eyeBtn} onClick={() => setGoster((v) => !v)}
                aria-label={goster ? 'Şifreyi gizle' : 'Şifreyi göster'} tabIndex={-1}
              >
                {goster ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </span>
            {capsLock && <span className={s.caps}><AlertTriangle size={13} /> Caps Lock açık</span>}
          </label>

          <label className={s.check}>
            <input type="checkbox" className={s.checkInput} checked={hatirla}
                   onChange={(e) => setHatirla(e.target.checked)} />
            <span className={s.box} aria-hidden="true"><CheckCircle2 size={14} /></span>
            <span className={s.checkText}>Beni hatırla</span>
          </label>

          <div className={`${s.kvkk} ${hata && hata.kvkk ? s.kvkkErr : ''}`}>
            <label className={s.check}>
              <input type="checkbox" className={s.checkInput} checked={kvkk}
                     onChange={(e) => { setKvkk(e.target.checked); if (e.target.checked && hata && hata.kvkk) setHata(null); }} />
              <span className={s.box} aria-hidden="true"><CheckCircle2 size={14} /></span>
              <span className={s.checkText}>
                <button type="button" className={s.linkBtn} onClick={() => setKvkkAcik(true)}>
                  KVKK Aydınlatma Metni
                </button>’ni okudum, onaylıyorum.
              </span>
            </label>
          </div>

          <button type="submit" className={s.submit} disabled={yukleniyor || kilitKalan > 0}>
            {yukleniyor ? (
              <><Loader2 size={18} className={s.spin} /> Giriş yapılıyor…</>
            ) : kilitKalan > 0 ? (
              <><Lock size={17} /> {sureYaz(kilitKalan)} sonra tekrar deneyin</>
            ) : (
              <>Giriş Yap <ArrowRight size={18} /></>
            )}
          </button>

          <p className={s.help}>
            <Phone size={13} /> Hesabınızla ilgili destek: <a href={DESTEK_TEL_HREF}>{DESTEK_TEL}</a>
          </p>
        </form>

        <p className={s.secure}><ShieldCheck size={14} /> Bağlantınız şifreli ve güvenli</p>
        <p className={s.mobileCopy}>© {yil} AS Otomotiv</p>
      </main>

      {kvkkAcik && <KvkkPenceresi onKapat={kvkkKapat} onOnayla={kvkkOnayla} />}
      {borc && <BorcPenceresi r={borc.r} onDevam={borcDevam} onKapat={borcKapat} />}
    </div>
  );
}
